import { useState, useEffect, useCallback, useRef } from 'react';
import nifty50LtpService from '../services/nifty50LtpService';

interface UseNifty50LtpOptions {
  autoStart?: boolean;
  updateInterval?: number; // For polling mode
  useStreaming?: boolean; // Use Server-Sent Events vs polling
}

interface Nifty50LtpState {
  ltp: number;
  timestamp: number;
  isLoading: boolean;
  error: string | null;
  isDataFresh: boolean;
  lastUpdateAgo: number;
  responseTime?: number;
}

export const useNifty50Ltp = (options: UseNifty50LtpOptions = {}) => {
  const {
    autoStart = true,
    updateInterval = 50,
    useStreaming = true
  } = options;

  const [state, setState] = useState<Nifty50LtpState>({
    ltp: 0,
    timestamp: 0,
    isLoading: false,
    error: null,
    isDataFresh: false,
    lastUpdateAgo: 0
  });

  const stopPollingRef = useRef<(() => void) | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Get instant LTP
  const getInstantLtp = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const ltp = await nifty50LtpService.getInstantLtp();
      setState(prev => ({
        ...prev,
        ltp,
        timestamp: Date.now(),
        isLoading: false,
        isDataFresh: true,
        lastUpdateAgo: 0
      }));
      return ltp;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message,
        isLoading: false
      }));
      throw error;
    }
  }, []);

  // Get LTP with metrics
  const getInstantLtpWithMetrics = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const data = await nifty50LtpService.getInstantLtpWithMetrics();
      setState(prev => ({
        ...prev,
        ltp: data.ltp,
        timestamp: data.timestamp,
        responseTime: data.responseTime,
        isLoading: false,
        isDataFresh: data.isDataFresh,
        lastUpdateAgo: data.lastUpdateAgo
      }));
      return data;
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.message,
        isLoading: false
      }));
      throw error;
    }
  }, []);

  // Start real-time updates
  const startRealTime = useCallback(() => {
    // Stop any existing connections
    stopRealTime();

    if (useStreaming) {
      // Use Server-Sent Events
      nifty50LtpService.startRealTimeStream(
        (data) => {
          setState(prev => ({
            ...prev,
            ltp: data.ltp,
            timestamp: data.timestamp,
            isDataFresh: true,
            lastUpdateAgo: Date.now() - data.timestamp,
            error: null
          }));
        },
        (error) => {
          setState(prev => ({
            ...prev,
            error: 'Real-time stream error',
            isDataFresh: false
          }));
        }
      );
    } else {
      // Use polling
      stopPollingRef.current = nifty50LtpService.startPolling(
        updateInterval,
        (ltp) => {
          setState(prev => ({
            ...prev,
            ltp,
            timestamp: Date.now(),
            isDataFresh: true,
            lastUpdateAgo: 0,
            error: null
          }));
        }
      );
    }
  }, [useStreaming, updateInterval]);

  // Stop real-time updates
  const stopRealTime = useCallback(() => {
    if (useStreaming) {
      nifty50LtpService.stopRealTimeStream();
    }
    
    if (stopPollingRef.current) {
      stopPollingRef.current();
      stopPollingRef.current = null;
    }

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, [useStreaming]);

  // Auto-start effect
  useEffect(() => {
    if (autoStart) {
      startRealTime();
    }

    return () => {
      stopRealTime();
    };
  }, [autoStart, startRealTime, stopRealTime]);

  // Update lastUpdateAgo periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setState(prev => ({
        ...prev,
        lastUpdateAgo: prev.timestamp ? Date.now() - prev.timestamp : 0
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return {
    // State
    ltp: state.ltp,
    timestamp: state.timestamp,
    isLoading: state.isLoading,
    error: state.error,
    isDataFresh: state.isDataFresh,
    lastUpdateAgo: state.lastUpdateAgo,
    responseTime: state.responseTime,

    // Actions
    getInstantLtp,
    getInstantLtpWithMetrics,
    startRealTime,
    stopRealTime,

    // Utilities
    isConnected: state.isDataFresh && !state.error,
    formattedLtp: state.ltp.toFixed(2),
    ageInSeconds: Math.floor(state.lastUpdateAgo / 1000)
  };
};